import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

/**
 * FaviconUploader
 *
 * Uploads a source image to the favicon endpoint, which generates all standard
 * favicon sizes on the server:
 *   favicon.ico  (16, 32, 48 px – multi-size ICO)
 *   favicon-16x16.png
 *   favicon-32x32.png
 *   apple-touch-icon.png     (180 × 180)
 *   android-chrome-192x192.png
 *   android-chrome-512x512.png
 *
 * Props:
 *   websiteId  – required, passed in the upload URL
 *   onUploaded – called with the server response `files` object on success
 *   onError    – called with an error message string on failure
 *   currentFaviconUrl – optional URL of the current 32-px favicon for preview
 */
export default function FaviconUploader({ websiteId, onUploaded, onError, currentFaviconUrl }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentFaviconUrl || null);

  const apiUrl = process.env.REACT_APP_API_URL || '';

  const upload = useCallback(async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file.');
      return;
    }
    if (!websiteId) {
      toast.error('Website ID is required for favicon upload.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('You must be authenticated to upload a favicon.');
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append('favicon', file);

    try {
      const resp = await fetch(`${apiUrl}public/images/${websiteId}/favicon`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!resp.ok) {
        let message = `Upload failed with status ${resp.status}`;
        try {
          const err = await resp.json();
          message = err?.message || err?.error || message;
        } catch (_) {}
        throw new Error(message);
      }

      const data = await resp.json();
      const faviconUrl = data?.url || data?.file?.url || data?.data?.url || null;

      // Show the 32-px favicon as a local preview immediately
      setPreviewUrl(faviconUrl);
      onUploaded?.(data);

      toast.success('Favicon updated successfully.');
    } catch (e) {
      const message = e?.message || 'Favicon upload failed.';
      toast.error(message);
      onError?.(message);
    } finally {
      setUploading(false);
    }
  }, [apiUrl, websiteId, onUploaded, onError]);

  const onInputChange = useCallback((e) => {
    const file = e.target.files?.[0];
    upload(file);
    e.target.value = '';
  }, [upload]);

  const openFileDialog = useCallback(() => {
    if (!uploading) inputRef.current?.click();
  }, [uploading]);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) setIsDragging(true);
  }, [uploading]);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    upload(file);
  }, [upload]);

  const zoneStyle = {
    border: `2px dashed ${isDragging ? '#2563eb' : '#cbd5e1'}`,
    borderRadius: 8,
    padding: '12px 16px',
    textAlign: 'center',
    cursor: uploading ? 'not-allowed' : 'pointer',
    color: '#475569',
    background: isDragging ? '#eff6ff' : 'transparent',
    transition: 'all 120ms ease-in-out',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={onInputChange}
        disabled={uploading}
        style={{ display: 'none' }}
      />
      <div
        role="button"
        tabIndex={0}
        aria-disabled={uploading}
        onClick={openFileDialog}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFileDialog(); } }}
        onDragOver={onDragOver}
        onDragEnter={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        style={zoneStyle}
      >
        {/* Current / preview favicon */}
        <div style={{
          width: 48, height: 48, flexShrink: 0,
          border: '1px solid #e5e7eb', borderRadius: 6,
          background: '#f8fafc',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
        }}>
          {previewUrl
            ? <img src={previewUrl} alt="favicon preview" style={{ width: 32, height: 32, objectFit: 'contain' }} />
            : <span style={{ fontSize: 22 }}>🖼</span>
          }
        </div>

        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: 14, fontWeight: 500 }}>
            {uploading ? 'Uploading…' : 'Click or drag & drop a favicon image'}
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
            Any image format · Generates ICO, PNG (16, 32, 180, 192, 512 px)
          </div>
        </div>
      </div>
    </div>
  );
}

FaviconUploader.propTypes = {
  websiteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onUploaded: PropTypes.func,
  onError: PropTypes.func,
  currentFaviconUrl: PropTypes.string,
};
